// Action Plan KPI aggregates — single source of truth for the metric
// cards on the Action Plan surfaces. Drives:
//   - The 5th "Workdays Recovered / Created" paired card in the
//     metric grid on audit/page.tsx + (public)/page.tsx +
//     results/page.tsx.
//   - The Time Budget body-card framing (values match its hours
//     directly /8) so the new paired card and the Time Budget card
//     tell the same story on the same page.
//
// Definitions match the Time Budget body card's `totalDiyHours` /
// `totalVaHours` directly (audit/page.tsx:2126-2127 and equivalents
// on /, /results). Set = Delegate + MustDelegate ONLY. Drop is NOT
// counted as "recovered" here because Drop = stop doing the task; no
// one is delivering those workdays back to your calendar. The
// paired-card framing is "workdays delivered by the delegation plan"
// — Drop doesn't deliver, it removes.
//
// Resolution semantics:
//   - Delegate / MustDelegate → recovered: diy_hours (founder hands
//                               the activity off; the full diy reclaim
//                               is theirs even when va < diy because
//                               the activity has been moved off the
//                               plate entirely)
//                              → created:   va_hours (hours the hire
//                               actually puts in)
//   - Drop      → recovered: 0, created: 0
//   - Keep/Plan → recovered: 0, created: 0
//
// SA Step 7's separate inline `hoursRecovered` computation
// (audit/page.tsx:3920+) uses a different framing — min(va,diy) for
// Delegate + diy for Drop+MustDelegate — and lives on the SA step,
// not the Action Plan. Outside this helper's scope; the SA strip is
// not consumed from here.
//
// delegationCost = sum over (Delegate + MustDelegate) of va_hours ×
// role_rate. Falls back to 13 when assigned_role_id is null —
// matches the canonical default used in the Action Plan roles table.

import { getFinalResolution } from "./resolution";

export interface ActionPlanRole {
  id: string;
  hourly_rate?: number | null;
}

export interface ActionPlanActivity {
  delegation_plan?: {
    assigned_role_id?: string | null;
    rova_tracker?: {
      diy_hours_monthly?: number | string | null;
      va_hours_monthly?: number | string | null;
    } | null;
  } | null;
  // Resolution / override fields read by getFinalResolution
  [key: string]: unknown;
}

export interface ActionPlanSessionContext {
  team_size_band: string | null;
  gross_revenue_band: string | null;
}

const HOURS_PER_WORKDAY = 8;

export interface ActionPlanKpis {
  /** Σ diy across Delegate + MustDelegate. Matches Time Budget body card's "Your Hours Recovered". */
  hoursRecovered: number;
  /** Σ va across Delegate + MustDelegate. Matches Time Budget body card's "Work Done For You". */
  workDoneForYou: number;
  /** hoursRecovered ÷ 8. Founder's reclaimed workdays per month. */
  workdaysRecovered: number;
  /** workDoneForYou ÷ 8. Workdays per month delivered by hires (or AI). */
  workdaysCreated: number;
  monthlyValue: number;
  delegationCost: number;
  annualImpact: number;
}

export function computeActionPlanKpis(
  activities: ActionPlanActivity[],
  roles: ActionPlanRole[],
  founderRate: number,
  sessionContext: ActionPlanSessionContext,
): ActionPlanKpis {
  let hoursRecovered = 0;
  let workDoneForYou = 0;
  let delegationCost = 0;

  for (const a of activities) {
    const res = getFinalResolution(a as never, sessionContext);
    const diy = Number(a.delegation_plan?.rova_tracker?.diy_hours_monthly) || 0;
    const va = Number(a.delegation_plan?.rova_tracker?.va_hours_monthly) || 0;

    if (res === "Delegate" || res === "MustDelegate") {
      hoursRecovered += diy;
      workDoneForYou += va;

      const roleId = a.delegation_plan?.assigned_role_id ?? null;
      const roleRate = roleId ? (roles.find((r) => r.id === roleId)?.hourly_rate ?? 13) : 13;
      delegationCost += va * Number(roleRate);
    }
  }

  const workdaysRecovered = hoursRecovered / HOURS_PER_WORKDAY;
  const workdaysCreated = workDoneForYou / HOURS_PER_WORKDAY;
  const monthlyValue = hoursRecovered * founderRate;
  const annualImpact = (monthlyValue - delegationCost) * 12;

  return {
    hoursRecovered,
    workDoneForYou,
    workdaysRecovered,
    workdaysCreated,
    monthlyValue,
    delegationCost,
    annualImpact,
  };
}
